import { useCallback, useMemo } from 'react';
import {
    Stack,
    IPersonaProps,
    Text,
    mergeStyleSets,
    Checkbox,
    CompactPeoplePicker,
    IBasePickerSuggestionsProps,
    mergeStyles,
    PersonaSize,
    PersonaPresence,
    Persona,
    IconButton,
} from '@fluentui/react';
import { TimeUser } from '../../../../../api/generated/data-contracts';
import { useConfigValuesState } from '../../../../../hooks/useConfigValue';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/api';
import { useUiContext } from '../../../../../components/Contexts/UIContext/uiCtx';
import { FunctionOr } from '../../../../../types/FunctionOr';
import { mapUserToPersona } from '../../../../../helpers/mapUserToPersona';

const styles = mergeStyleSets({
    description: {
        fontSize: '14px',
        fontWeight: 500,
        opacity: 0.8,
        marginBottom: '30px',
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        marginTop: '16px',
        marginBottom: '8px',
    },
    checkboxContainer: {
        marginBottom: '16px',
    },
    checkboxDescription: {
        fontSize: '12px',
        opacity: 0.5,
        marginTop: '4px',
        marginLeft: '28px',
    },
    autoApproveLabel: {
        marginRight: '8px',
    },
    peoplePickerContainer: {
        marginTop: '8px',
    },
});

export const ApproverConfiguration = ({ user, setUser }: { user: TimeUser; setUser: (update: FunctionOr<TimeUser>) => void }) => {
    const {
        initialData: {
            aggregatedUser: { originalUser },
        },
        setConfigValueCache,
    } = useConfigValuesState();
    const { mutateAsync, data: userResult } = useMutation((searchText: string) => ApiCalls.searchUsers({ searchText }).then(res => res.data));
    const onResolveSuggestions = useCallback(
        (searchText: string) => {
            if (searchText) {
                return mutateAsync(searchText).then(users => users.map(mapUserToPersona));
            }
            // Returning null makes the picker do nothing. This is undocumented but is in the source code
            return null;
        },
        [mutateAsync],
    );

    // Mutation for setting auto approve
    const { mutate: setAutoApprove } = useMutation({
        mutationFn: ({ autoApprove, userId }: { autoApprove: boolean; userId: string }) =>
            ApiCalls.setAutoApprovalForUser({ autoApprove, userId }).then(() => userId),
    });

    const handleAutoApproveChange = useCallback(
        (checked: boolean) => {
            setAutoApprove({ autoApprove: checked, userId: user.id });
            setUser(user => ({ ...user, autoApprove: checked }));
            if (originalUser.id === user.id) {
                setConfigValueCache(initData => ({
                    ...initData,
                    aggregatedUser: {
                        ...initData.aggregatedUser,
                        originalUser: {
                            ...initData.aggregatedUser.originalUser,
                            autoApprove: checked,
                        },
                    },
                }));
            }
        },
        [originalUser.id, setAutoApprove, setConfigValueCache, setUser, user.id],
    );

    const { isDarkmode } = useUiContext();
    const suggestionProps = useMemo<IBasePickerSuggestionsProps>(
        () => ({
            suggestionsHeaderText: 'Suggested People',
            mostRecentlyUsedHeaderText: 'Suggested Contacts',
            noResultsFoundText: 'No results found',
            loadingText: 'Loading',
            showRemoveButtons: false,
            suggestionsAvailableAlertText: 'People Picker Suggestions available',
            suggestionsContainerAriaLabel: 'Suggested contacts',
            resultsMaximumNumber: resultsMaximumNumber,
            searchForMoreText: 'Get all results',
            searchingText: 'Fetching all results...',
            resultsFooterFull: () => {
                return (
                    <div style={{ textAlign: 'center', color: isDarkmode ? 'rgb(208, 208, 208)' : 'rgb(96, 94, 92)', fontSize: '12px', lineHeight: '30ox' }}>
                        Max results reached, please specify search
                    </div>
                );
            },
            resultsFooter: () => {
                return (
                    <div style={{ textAlign: 'center', color: isDarkmode ? 'rgb(208, 208, 208)' : 'rgb(96, 94, 92)', fontSize: '12px', lineHeight: '30ox' }}>
                        All results shown
                    </div>
                );
            },
        }),
        [isDarkmode],
    );

    const onAddApprover = useCallback(
        (persona: IPersonaProps): void => {
            if (user.id !== persona.itemID) {
                ApiCalls.setApprover({ approverUserId: persona.itemID, userId: user.id });
            } else {
                ApiCalls.setAutoApprovalForUser({ autoApprove: true, userId: user.id });
            }
            const approver = userResult.find(user => user.id === persona.itemID);
            setUser(user => ({ ...user, approver }));
            if (persona.itemID === originalUser.id) {
                setConfigValueCache(initData => ({
                    ...initData,
                    aggregatedUser: {
                        ...initData.aggregatedUser,
                        originalUser: {
                            ...initData.aggregatedUser.originalUser,
                            approver,
                        },
                    },
                }));
            }
        },
        [originalUser.id, setConfigValueCache, setUser, user.id, userResult],
    );

    const onRemoveApprover = useCallback((): void => {
        ApiCalls.setApprover({ approverUserId: null, userId: user.id });
        setUser(user => ({ ...user, approver: null }));
        if (user.id === originalUser.id) {
            setConfigValueCache(initData => ({
                ...initData,
                aggregatedUser: {
                    ...initData.aggregatedUser,
                    originalUser: {
                        ...initData.aggregatedUser.originalUser,
                        approver: null,
                    },
                },
            }));
        }
    }, [originalUser.id, setConfigValueCache, setUser, user.id]);

    const approverPersona = useMemo(() => {
        if (user.approver) {
            return mapUserToPersona(user.approver);
        }
    }, [user.approver]);

    return (
        <>
            <Stack>
                <Text className={styles.description}>Configure approval settings and assign approver for the user</Text>
            </Stack>

            <Stack className={styles.checkboxContainer}>
                <Checkbox label="Auto Approve" checked={user.autoApprove} onChange={(_, checked) => handleAutoApproveChange(checked)} />
                <Text className={styles.checkboxDescription}>Automatically approve the user's timesheet submissions</Text>
            </Stack>

            <Stack>
                <Text className={styles.sectionTitle}>Approver</Text>
                <Stack className={styles.peoplePickerContainer}>
                    <CompactPeoplePicker
                        styles={{ root: { maxWidth: 600, width: '100%' } }}
                        onResolveSuggestions={onResolveSuggestions}
                        itemLimit={10}
                        getTextFromItem={getTextFromItem}
                        pickerSuggestionsProps={suggestionProps}
                        className={'ms-PeoplePicker'}
                        inputProps={{
                            'aria-label': 'People Picker',
                            placeholder: 'Search here to select an approver...',
                        }}
                        resolveDelay={500}
                        onItemSelected={persona => {
                            if (persona) {
                                onAddApprover(persona);
                                return null; // Clear the input after selection
                            }
                            return persona;
                        }}
                    />

                    {approverPersona && (
                        <Stack className={approverListStyles}>
                            <Stack horizontal className={approverItemStyles}>
                                <Persona
                                    imageInitials={approverPersona.imageInitials}
                                    text={approverPersona.text}
                                    secondaryText={approverPersona.secondaryText}
                                    size={PersonaSize.size32}
                                    presence={PersonaPresence.none}
                                />
                                <IconButton iconProps={{ iconName: 'Cancel' }} title="Remove approver" ariaLabel="Remove approver" onClick={onRemoveApprover} />
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </>
    );
};

const approverListStyles = mergeStyles({
    marginTop: '16px',
    border: '1px solid #edebe9',
    borderRadius: '2px',
    padding: '8px',
    maxHeight: '200px',
    overflowY: 'auto',
});

const approverItemStyles = mergeStyles({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px',
    margin: '4px 0',
    borderRadius: '2px',
    ':hover': {
        backgroundColor: '#f3f2f1',
    },
});

const resultsMaximumNumber: number = 50;

function getTextFromItem(persona: IPersonaProps): string {
    return persona.text;
}
